import React, { useState } from 'react';
import type { ComparisonScope, MarketProfileState } from '../marketProfileStore';
import {
  componentMarketStatus,
  compensationHeadline,
  computeTotalComp,
  formatRub,
  isInsufficientSample,
  isProfileOutdated,
  marketDataInsight,
  percentileToLabel,
  stablePercentileForScope,
  stableSampleSizeForScope,
  type ComponentMarketStatus,
  positionFixValue,
  positionHasLtip,
  positionLtipAmountValue,
  positionStiValue,
} from '../marketProfileStore';
import { SensitiveDataNotice } from '../MarketProfileShared';

type Props = {
  state: MarketProfileState;
  confirmed: boolean;
  onConfirmReview?: () => void;
};

function statusBadgeClass(status: ComponentMarketStatus | ReturnType<typeof percentileToLabel>): string {
  if (status === 'ниже рынка') return 'below';
  if (status === 'в рынке') return 'in';
  if (status === 'выше рынка' || status === 'выше медианы') return 'above';
  return 'muted';
}

function ComparisonScopeToggle({
  scope,
  onChange,
}: {
  scope: ComparisonScope;
  onChange: (scope: ComparisonScope) => void;
}) {
  return (
    <div className="dcf-mp-md-scope">
      <p className="dcf-mp-md-scope__label">Группа сравнения</p>
      <div className="dcf-mp-option-grid dcf-mp-md-scope__grid">
        <button
          type="button"
          className={`dcf-mp-option-card ${scope === 'industry' ? 'dcf-mp-option-card--active' : ''}`}
          onClick={() => onChange('industry')}
        >
          <span className="dcf-mp-md-scope__title">Вся инвестиционная индустрия</span>
          <span className="dcf-mp-md-scope__desc">
            Сравнение с релевантными ролями по всей инвестиционной индустрии
          </span>
        </button>
        <button
          type="button"
          className={`dcf-mp-option-card ${scope === 'sector' ? 'dcf-mp-option-card--active' : ''}`}
          onClick={() => onChange('sector')}
        >
          <span className="dcf-mp-md-scope__title">Мой сектор</span>
          <span className="dcf-mp-md-scope__desc">
            Сравнение только с участниками из вашего сектора
          </span>
        </button>
      </div>
    </div>
  );
}

function QuartileChart({ percentile }: { percentile: number }) {
  const markers = [
    { pos: 25, label: 'P25' },
    { pos: 50, label: 'P50' },
    { pos: 75, label: 'P75' },
    { pos: 90, label: 'P90' },
  ];
  return (
    <div className="dcf-mp-quartile">
      <div className="dcf-mp-quartile__track">
        {markers.map((m) => (
          <div key={m.label} className="dcf-mp-quartile__tick" style={{ left: `${m.pos}%` }}>
            <span className="dcf-mp-quartile__tick-line" />
            <span className="dcf-mp-quartile__tick-label">{m.label}</span>
          </div>
        ))}
        <div className="dcf-mp-quartile__user" style={{ left: `${percentile}%` }} title="Ваше положение">
          <span className="dcf-mp-quartile__user-dot" />
          <span className="dcf-mp-quartile__user-label">Вы</span>
        </div>
      </div>
      <div className="dcf-mp-quartile__legend">
        <span>нижний квартиль</span>
        <span>медиана</span>
        <span>верхний квартиль</span>
        <span>высокий диапазон</span>
      </div>
    </div>
  );
}

function ComponentRow({
  label,
  value,
  status,
}: {
  label: string;
  value: string;
  status: ComponentMarketStatus;
}) {
  const fill = status === 'недостаточно данных' ? 0 : status === 'ниже рынка' ? 28 : status === 'в рынке' ? 52 : 72;
  return (
    <div className="dcf-mp-comp-row">
      <div className="dcf-mp-comp-row__head">
        <span className="dcf-mp-comp-row__name">{label}</span>
        <span className={`dcf-mp-comp-row__badge dcf-mp-comp-row__badge--${statusBadgeClass(status)}`}>
          {status}
        </span>
      </div>
      <div className="dcf-mp-comp-row__value">{value}</div>
      {status !== 'недостаточно данных' && (
        <div className="dcf-mp-comp-row__bar">
          <div className="dcf-mp-comp-row__fill" style={{ width: `${fill}%` }} />
        </div>
      )}
    </div>
  );
}

export default function MarketDataOpenDashboard({
  state,
  confirmed,
  onConfirmReview,
}: Props) {
  const [scope, setScope] = useState<ComparisonScope>('sector');

  const { data } = state.position;
  const percentile = stablePercentileForScope(state, scope);
  const label = percentileToLabel(percentile);
  const sampleSize = stableSampleSizeForScope(state, scope);
  const outdated = isProfileOutdated(state);
  const insufficient = isInsufficientSample(sampleSize) && scope === 'sector';
  const total = computeTotalComp(data);

  const statusBadge = outdated
    ? 'Требуется обновление данных'
    : confirmed
      ? 'Данные открыты'
      : 'Предварительный просмотр';

  return (
    <div className="dcf-mp-md-open">
      {/* Summary */}
      <section className="dcf-mp-card dcf-mp-md-summary">
        <div className="dcf-mp-md-summary__main">
          <h1 className="dcf-mp-hero__title">Рыночные данные</h1>
          <p className="dcf-mp-hero__desc">
            Сравните компенсацию и уровень роли с релевантной рыночной группой.
          </p>
          <p className="dcf-mp-md-summary__note">
            Расчёт основан на данных вашей роли, компенсации и результатах грейдинга.
          </p>
        </div>
        <span className={`dcf-mp-access-badge ${confirmed && !outdated ? 'dcf-mp-access-badge--open' : ''}`}>
          <i className={`fas ${confirmed && !outdated ? 'fa-circle-check' : outdated ? 'fa-clock-rotate-left' : 'fa-chart-line'}`} aria-hidden="true" />
          {statusBadge}
        </span>
      </section>

      {!confirmed && onConfirmReview && (
        <div className="dcf-mp-notice dcf-mp-notice--ready">
          <i className="fas fa-circle-check" aria-hidden="true" />
          <span>Анкеты заполнены. Подтвердите данные, чтобы зафиксировать рыночные ориентиры.</span>
          <button type="button" className="dcf-btn dcf-btn--primary" onClick={onConfirmReview}>
            Проверить и подтвердить
          </button>
        </div>
      )}

      {outdated && (
        <div className="dcf-mp-notice dcf-mp-notice--outdated">
          <i className="fas fa-clock-rotate-left" aria-hidden="true" />
          <span>
            С момента последнего обновления прошло достаточно времени. Для актуализации данных
            обратитесь к администратору сообщества.
          </span>
        </div>
      )}

      <ComparisonScopeToggle scope={scope} onChange={setScope} />

      {insufficient && (
        <section className="dcf-mp-card dcf-mp-md-insufficient">
          <h2 className="dcf-mp-card__title">Недостаточно данных для точного сравнения</h2>
          <p className="dcf-mp-action-card__desc">
            В выбранной группе пока недостаточно профилей. Мы можем показать более широкий
            ориентир по всей инвестиционной индустрии или предложить изменить группу сравнения.
          </p>
          <div className="dcf-mp-md-insufficient__actions">
            <button type="button" className="dcf-btn dcf-btn--secondary" onClick={() => setScope('industry')}>
              Смотреть по всей инвестиционной индустрии
            </button>
          </div>
        </section>
      )}

      <section className="dcf-mp-card dcf-mp-md-panel dcf-mp-md-panel--wide">
          <h2 className="dcf-mp-card__title">Компенсация относительно рынка</h2>
          <p className="dcf-mp-form-hint">
            Сравнение построено по роли, уровню и выбранной группе рынка.
          </p>

          <div className="dcf-mp-result-status dcf-mp-md-status">
            <span className={`dcf-mp-market-badge dcf-mp-market-badge--${statusBadgeClass(label)}`}>
              {label}
            </span>
            <p className="dcf-mp-md-status__headline">{compensationHeadline(percentile)}</p>
            <span className="dcf-mp-result-status__note">предварительная оценка</span>
          </div>

          <QuartileChart percentile={percentile} />
          <p className="dcf-mp-percentile__value">Ориентировочный перцентиль: ~{percentile}</p>

          <div className="dcf-mp-md-comp-rows">
            <ComponentRow label="FIX" value={formatRub(positionFixValue(data))} status={componentMarketStatus(state, 'fix', scope)} />
            <ComponentRow
              label="STI"
              value={positionStiValue(data) ? `${positionStiValue(data)}% от base` : '—'}
              status={componentMarketStatus(state, 'sti', scope)}
            />
            <ComponentRow
              label="LTIP"
              value={positionHasLtip(data) ? formatRub(positionLtipAmountValue(data)) : '—'}
              status={componentMarketStatus(state, 'ltip', scope)}
            />
            <ComponentRow
              label="Total Comp"
              value={formatRub(total)}
              status={componentMarketStatus(state, 'total', scope)}
            />
          </div>

          <div className="dcf-mp-md-insight">
            <i className="fas fa-lightbulb" aria-hidden="true" />
            <span>{marketDataInsight(state, scope, sampleSize)}</span>
          </div>
        </section>

      <SensitiveDataNotice compact />
    </div>
  );
}
